import { X, Download, Heart, Share2, ExternalLink, Eye } from 'lucide-react';
import { useLocalStorage } from '../hooks/useLocalStorage';

// Modal to show full image details when user clicks on an image
const ImageModal = ({ image, isOpen, onClose, onDownload, addToast }) => {
  const [favorites, setFavorites] = useLocalStorage('favorites', []);
  const isFavorited = favorites.some((fav) => fav.id === image?.id);

  if (!image || !isOpen) return null;

  // Add or remove from favorites
  const handleFavorite = () => {
    if (isFavorited) {
      setFavorites(favorites.filter((fav) => fav.id !== image.id));
    } else {
      setFavorites([...favorites, image]);
    }
  };

  // Share image using native share or copy link
  const handleShare = async () => {
    const url = image.links?.html || window.location.href;
    const title = image.alt_description || 'Check out this image on PixelBazar';
    
    if (navigator.share) {
      try {
        await navigator.share({ title: 'PixelBazar', text: title, url });
        if (addToast) addToast('Image shared!', 'success');
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      try {
        await navigator.clipboard.writeText(`${title}\n${url}`);
        if (addToast) addToast('Link copied!', 'success');
      } catch (error) {
        if (addToast) addToast('Failed to copy', 'error');
      }
    }
  };

  return (
    <>
      {/* Dark background overlay */}
      <div className="fixed inset-0 bg-black/80 z-40 backdrop-blur-md" onClick={onClose} />

      {/* Modal container */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
        <div className="bg-white dark:bg-gray-950 rounded-2xl max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col lg:flex-row shadow-2xl relative">
          
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2.5 bg-white dark:bg-gray-900 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition shadow-lg"
          >
            <X size={24} className="text-gray-900 dark:text-white" />
          </button>

          {/* Image section */}
          <div className="w-full lg:w-3/5 bg-gray-100 dark:bg-gray-900 flex items-center justify-center overflow-hidden relative group">
            <img
              src={image.urls.regular}
              alt={image.alt_description || 'Image'}
              className="w-full h-full object-contain"
            />
            
            {/* Image title on hover */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 sm:p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <p className="text-white text-sm sm:text-base font-medium">{image.alt_description || 'Untitled'}</p>
            </div>
          </div>

          {/* Details section */}
          <div className="w-full lg:w-2/5 p-4 sm:p-6 lg:p-8 overflow-y-auto flex flex-col justify-between bg-white dark:bg-gray-950">
            
            {/* Header info */}
            <div>
              {/* Title */}
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4 line-clamp-2">
                {image.alt_description || 'Untitled Image'}
              </h2>

              {/* Photographer info */}
              <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-200 dark:border-gray-800">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
                  {image.user?.name?.charAt(0) || '?'}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {image.user?.name || 'Unknown'}
                  </p>
                  <a
                    href={image.user?.links?.html}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                  >
                    View Profile <ExternalLink size={12} />
                  </a>
                </div>
              </div>

              {/* Stats - only show if data exists */}
              {(image.views || image.downloads) && (
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {image.views && (
                    <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-900 dark:to-gray-800 rounded-xl text-center">
                      <Eye size={16} className="text-blue-600 dark:text-blue-400 mx-auto mb-1" />
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Views</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {(image.views / 1000).toFixed(1)}K
                      </p>
                    </div>
                  )}
                  {image.downloads && (
                    <div className="p-3 bg-gradient-to-br from-green-50 to-green-100 dark:from-gray-900 dark:to-gray-800 rounded-xl text-center">
                      <Download size={16} className="text-green-600 dark:text-green-400 mx-auto mb-1" />
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Downloads</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {(image.downloads / 1000).toFixed(1)}K
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Image info */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-xl">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Size</p>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {image.width} × {image.height}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Category</p>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {image.category || 'General'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Description */}
              {image.description && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Description
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-4">
                    {image.description}
                  </p>
                </div>
              )}

              {/* Tags */}
              {image.tags && image.tags.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {image.tags.slice(0, 6).map((tag) => (
                      <span
                        key={tag.title}
                        className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition cursor-pointer"
                      >
                        #{tag.title}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-3 pt-6 border-t border-gray-200 dark:border-gray-800">
              <button
                onClick={() => onDownload(image)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-gray-900 to-gray-800 dark:from-white dark:to-gray-100 text-white dark:text-black rounded-xl hover:shadow-lg transition font-semibold text-base"
              >
                <Download size={20} />
                Download
              </button>
              <div className="flex gap-3">
                <button
                  onClick={handleFavorite}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition font-medium text-sm ${
                    isFavorited
                      ? 'bg-red-500 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <Heart size={18} fill={isFavorited ? 'currentColor' : 'none'} />
                  Save
                </button>
                <button
                  onClick={handleShare}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition font-medium text-sm cursor-pointer"
                >
                  <Share2 size={18} />
                  Share
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImageModal;
